// NPM packages
import React from 'react';
import { styled } from '@mui/system';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { animateScroll as scroll } from 'react-scroll';
import { useParams } from 'react-router-dom';

// All other imports
import NavItem from 'types/NavItem';
import { default as UnstyledHeader } from './header/Header';
import { default as UnstyledFooter } from './footer/Footer';
import Home from 'components/pages/Home';
import SignUp from 'components/pages/SignUp';
import Projects from './pages/projects/Projects';
import ResendVerification from 'components/pages/ResendVerification';
import NotFound from 'components/pages/NotFound';
import ComingSoon from 'components/pages/ComingSoon';
import VerifyEmail from 'components/pages/VerifyEmail';
import ForgotPassword from 'components/pages/ForgotPassword';
import ResetPassword from 'components/pages/ResetPassword';
import ProjectInfo from 'components/pages/projectDetails/ProjectInfo';
import ProjectMembers from 'components/pages/projectDetails/ProjectMembers';
import AuthGuard from 'components/core/AuthGuard';
import GlobalErrorBar from 'components/core/GlobalErrorBar';
import ConfirmationModal from 'components/core/ConfirmationModal';
import Disclaimer from 'components/pages/Disclaimer';
import Metrics from 'components/pages/metrics/Metrics';
import Profile from 'components/pages/Profile';
import StaticPage from 'components/pages/StaticPage';
import ReviewNewUser from 'components/pages/ReviewNewUser';
import ManageUserMemberships from 'components/pages/UserMemberships/ManageUserMemberships';
import Acknowledgement from 'components/pages/Acknowledgement';
import Events from './news-and-events/Events';

// Custom landing pages for projects
import BSECProject from 'components/pages/projectDetails/custom-landing-pages/bsec/BSECLanding';
import { TierGateProvider } from './pages/UserMemberships/TierGateProvider';
import DocsEmbed from 'components/pages/DocsEmbed';

const Root = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  minHeight: '100vh',
  fontFamily: "'Roboto', sans-serif",
  fontSize: '1.2rem',
  fontWeight: 400,
});
const Header = styled(UnstyledHeader)({
  flex: '0 0 auto',
});
const Body = styled('div')({
  flex: '1 0 auto',
});
const Footer = styled(UnstyledFooter)({
  flex: '0 0 auto',
});

export interface AppProps {
  className?: string;
}

const headerItems: NavItem[] = [
  {
    text: 'About',
    href: '/about',
  },
  {
    text: 'Events',
    href: '/events',
  },
  {
    text: 'Policies',
    href: '/policies',
  },
  {
    text: 'Metrics',
    href: '/metrics',
  },
  {
    text: 'Acknowledgement',
    href: '/acknowledgement',
  },
];

export function HelpRedirectToDocs(): null {
  const { '*': rest } = useParams();
  useEffect(() => {
    window.location.replace(
      `https://msd-live.github.io/msdlive-docs/${rest ?? ''}`
    );
  }, [rest]);
  return null;
}
export function ScrollToTop(): null {
    const { pathname } = useLocation();
    useEffect(() => {
        // Use react-scroll for better cross-browser compatibility
        scroll.scrollToTop({
            duration: 500,
            delay: 0,
            smooth: 'easeInOutQuart',
        });
    }, [pathname]);
    return null;
}

export default function App(props: AppProps): React.ReactElement | null {
  const { className } = props;
  return (
    <Root className={className}>
      <GlobalErrorBar />
      <ConfirmationModal />
      <AuthGuard>
      <TierGateProvider>
      <ScrollToTop />
        <Routes>
          <Route
            path="/minimal/*"
            element={
              <Body role="main">
                <StaticPage />
              </Body>
            }
          />
          <Route
            path="*"
            element={
              <>
                <Header items={headerItems} />
                <Body role="main">
                  <Routes>
                    <Route path="/" element={<Home />} />
                    <Route path="/sign-up" element={<SignUp />} />
                    <Route path="/approve-user" element={<ReviewNewUser />} />
                    <Route path="/manage-memberships" element={<ManageUserMemberships />} />
                    <Route path="/profile" element={<Profile />} />
                    <Route path="/disclaimer" element={<Disclaimer />} />
                    <Route path="/resend-verification" element={<ResendVerification />}/>
                    <Route path="/confirm-code" element={<VerifyEmail />} />
                    <Route path="/my-msd" element={<ComingSoon />} />
                    <Route path="/forgot-password" element={<ForgotPassword />} />
                    <Route path="/reset-password" element={<ResetPassword />} />
                    <Route path="/projects" element={<Projects />} />
                    <Route path="/projects/bsec" element={<BSECProject />} />
                    <Route path="/projects/bsec-stage" element={<BSECProject />} />
                    <Route path="/projects/bsec-dev" element={<BSECProject />} />
                    <Route path="/projects/:id" element={<Navigate to="info" replace={true} />} />
                    <Route path="/projects/:id/info" element={<ProjectInfo />} />
                    <Route path="/projects/:id/edit" element={<ProjectMembers />} />
                    <Route path="/projects/:id/current" element={<ProjectMembers />} />
                    <Route path="/projects/:id/pending" element={<ProjectMembers />} />
                    <Route path="/metrics" element={<Metrics />} />
                    <Route path="/about" element={<DocsEmbed path="platform_community/about/" />} />
                    <Route path="/policies" element={<DocsEmbed path="platform_community/policies/" />} />
                    <Route path="/acknowledgement" element={<Acknowledgement />} />
                    <Route path="/computational-resources" element={<DocsEmbed path="tools_services/computational_resources/" />} />
                    <Route path="/interactive-data-dashboards" element={<DocsEmbed path="tools_services/interactive_dashboards/" />} />
                    <Route path="/model-training-notebooks" element={<DocsEmbed path="tools_services/model_training_notebooks/" />} />
                    <Route path="/cli-help" element={<DocsEmbed path="tools_services/cli/installation/" />} />
                    <Route path="/spatial-metadata" element={<DocsEmbed path="for_data_providers/spatial_metadata/" />} />
                    <Route path="/notebook-lab" element={<DocsEmbed path="for_data_providers/launch_notebook_environment/" />} />
                    <Route path="/creating-dataset-notebooks" element={<DocsEmbed path="for_data_providers/create_example_notebooks/" />} />
                    <Route path="/events" element={<Events />} />
                    <Route path="/providing-pre-packaged-code" element={<DocsEmbed path="for_data_providers/create_example_notebooks/" />} />
                    <Route path="/get-started" element={<DocsEmbed path="" />} />
                    <Route path="/help/*" element={<HelpRedirectToDocs/>} />
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                </Body>
                <Footer />
              </>
            }
          />
        </Routes>
        </TierGateProvider>
      </AuthGuard>
    </Root>
  );
}